import { HttpEvent, HttpHandlerFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { selectAuthToken } from '../store/auth/selectors/auth.selectors';
import { AuthService } from './auth.service';
import * as AuthActions from '../store/auth/actions/auth.actions';
import * as ErrorActions from '../store/error/actions/error.actions';
import * as NotificationActions from '../store/notification/actions/notification.actions';
import { Notification } from '../store/notification/actions/notification.actions';
import { TokenModel } from '../model/token.model';
import { JwtHelperService } from './jwt-helper.service';

/**
 * Token Refresh Interceptor
 *
 * Automatically refreshes access tokens before they expire to provide
 * seamless authentication experience.
 *
 * Flow:
 * 1. Before each request, check if access token is expired or about to expire (< 1 minute left)
 * 2. If token needs refresh:
 *    - Call /auth/refresh endpoint with refresh token
 *    - Update token in NgRx store
 *    - Continue with original request using new access token
 * 3. If token is still valid, continue with original request
 *
 * Note: This runs BEFORE the auth interceptor that adds the Bearer token
 */
export function tokenRefreshIntercept(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const store = inject(Store);
  const authService = inject(AuthService);
  const jwtHelper = inject(JwtHelperService);

  // Skip refresh for auth endpoints (webauthn-login, signup, refresh, logout)
  if (
    req.url.includes('/webauthn-login') ||
    req.url.includes('/signup') ||
    req.url.includes('/auth/refresh') ||
    req.url.includes('/auth/logout')
  ) {
    return next(req);
  }

  return store.select(selectAuthToken).pipe(
    take(1),
    switchMap((token) => {
      // No token, proceed without refresh
      if (!token || !token.refreshToken) {
        return next(req);
      }

      // Check if token is expired or about to expire (< 60 seconds remaining)
      const now = Date.now();
      const expiresAt = token.expiresAt || 0;
      const timeUntilExpiry = expiresAt - now;
      const REFRESH_THRESHOLD = 60 * 1000; // 60 seconds

      // Token is still valid and not expiring soon
      if (timeUntilExpiry > REFRESH_THRESHOLD) {
        return next(req);
      }

      // Token expired or expiring soon - refresh it
      console.log('Access token expiring soon, refreshing...', {
        expiresAt: new Date(expiresAt),
        now: new Date(now),
        timeUntilExpiry: Math.floor(timeUntilExpiry / 1000) + 's',
        hasRefreshToken: !!token.refreshToken,
      });

      return authService.refreshAccessToken(token.refreshToken).pipe(
        catchError((refreshError) => {
          // Token refresh itself failed - logout user
          console.error('Token refresh failed, logging out', refreshError);

          // Show warning notification to user
          const notification: Notification = {
            id: Date.now().toString(),
            severity: 'warning',
            title: 'Session Expired',
            summary: 'Your session has expired.',
            detail:
              'For security reasons, you are being logged out. Please log in again to continue.',
            timestamp: new Date(),
            duration: 0, // Don't auto-dismiss - user needs to see this
          };
          store.dispatch(NotificationActions.ShowNotification({ notification }));

          // Dispatch error to store (this will show error modal for critical errors)
          if (refreshError instanceof HttpErrorResponse) {
            store.dispatch(
              ErrorActions.HttpError({
                httpError: refreshError,
                actionType: 'Automatic Token Refresh',
              }),
            );
          }

          // Refresh failed - likely refresh token is invalid/expired
          // Dispatch logout to clear state and redirect to login
          store.dispatch(AuthActions.logout());

          return throwError(() => refreshError);
        }),
        switchMap((refreshResponse) => {
          console.log('Token refreshed successfully');

          // Create updated token model
          const newToken = new TokenModel();
          newToken.accessToken = refreshResponse.accessToken;
          newToken.refreshToken = refreshResponse.refreshToken;
          newToken.tokenType = 'Bearer';
          newToken.expiresIn = refreshResponse.expiresIn;
          newToken.expiresAt = Date.now() + refreshResponse.expiresIn * 1000;

          // Decode JWT to extract roles and username
          newToken.roles = jwtHelper.getRoles(refreshResponse.accessToken);
          newToken.username = jwtHelper.getUsername(refreshResponse.accessToken) || '';

          // Update token in store
          store.dispatch(AuthActions.tokenRefreshed({ token: newToken }));

          // Clone request with new Authorization header
          // Don't rely on auth interceptor - add token here directly
          const clonedReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken.accessToken}`,
            },
          });

          // Continue with updated request containing new token
          return next(clonedReq);
        }),
      );
    }),
  );
}
